import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useDataSourceQuery } from '@/hooks/useDataSources'
import { useDatabasesQuery } from '@/hooks/useDatabase'
import { useToast } from '@/hooks/useToast'
import { AgentProxyService } from '@/services/agentProxyService'
import {
  ArrowLeftIcon,
  UserIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  CheckIcon,
  XMarkIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

interface User {
  user_name: string
  host?: string
  privileges?: string[]
}

interface Role {
  role_name: string
  privileges?: string[]
}

interface Grant {
  privilege: string
  object_type: string
  object_name: string
  schema_name?: string
  grant_option?: boolean
}

export function RBACMatrix() {
  const { connectionId } = useParams<{ connectionId: string }>()
  const { showToast } = useToast()
  
  const [selectedDatabase, setSelectedDatabase] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [users, setUsers] = useState<User[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [privileges, setPrivileges] = useState<string[]>([])
  const [userPrivileges, setUserPrivileges] = useState<Map<string, Set<string>>>(new Map())
  const [rolePrivileges, setRolePrivileges] = useState<Map<string, Set<string>>>(new Map())
  
  // Fetch connection details
  const { data: dataSource } = useDataSourceQuery(connectionId || '')
  
  // Fetch databases
  const { data: databases, isLoading: isLoadingDatabases } = useDatabasesQuery(connectionId || '')
  const databasesList = useMemo(() => (Array.isArray(databases) ? databases : []), [databases])
  
  const isPostgreSQL = dataSource?.databaseType === 'PostgreSQL'
  const isMySQL = dataSource?.databaseType === 'MySQL'
  
  // Auto-select first database
  useEffect(() => {
    if (!selectedDatabase && databasesList.length > 0 && !isLoadingDatabases) {
      const firstDbName = databasesList[0].name
      if (firstDbName) {
        setSelectedDatabase(firstDbName)
      }
    }
  }, [databasesList, selectedDatabase, isLoadingDatabases])
  
  // Load all data
  const loadData = useCallback(async () => {
    if (!connectionId || !selectedDatabase) return
    
    setIsLoading(true)
    try {
      // Load users
      const usersResponse = await AgentProxyService.getDatabaseUsers(connectionId, selectedDatabase)
      let usersList: User[] = []
      if (usersResponse && typeof usersResponse === 'object') {
        if ('users' in usersResponse) {
          usersList = ((usersResponse as { users?: User[] }).users) ?? []
        } else if ('result' in usersResponse && (usersResponse as { result?: { users?: User[] } }).result?.users) {
          usersList = ((usersResponse as { result?: { users?: User[] } }).result?.users) ?? []
        }
      }
      setUsers(usersList)
      
      // Load roles (PostgreSQL only)
      let rolesList: Role[] = []
      if (isPostgreSQL) {
        const rolesResponse = await AgentProxyService.getRoles(connectionId, selectedDatabase)
        if (rolesResponse && typeof rolesResponse === 'object') {
          if ('roles' in rolesResponse) {
            rolesList = ((rolesResponse as { roles?: Role[] }).roles) ?? []
          } else if ('result' in rolesResponse && (rolesResponse as { result?: { roles?: Role[] } }).result?.roles) {
            rolesList = ((rolesResponse as { result?: { roles?: Role[] } }).result?.roles) ?? []
          }
        }
        setRoles(rolesList)
      }
      
      // Load privileges for each user
      const userPrivMap = new Map<string, Set<string>>()
      const allPrivileges = new Set<string>()
      
      for (const user of usersList) {
        if (user.user_name) {
          try {
            const grantsResponse = await AgentProxyService.getGrants(
              connectionId,
              selectedDatabase,
              user.user_name,
              'user'
            )
            
            let grants: Grant[] = []
            if (grantsResponse && typeof grantsResponse === 'object') {
              if ('grants' in grantsResponse) {
                grants = ((grantsResponse as { grants?: Grant[] }).grants) ?? []
              } else if ('result' in grantsResponse && (grantsResponse as { result?: { grants?: Grant[] } }).result?.grants) {
                grants = ((grantsResponse as { result?: { grants?: Grant[] } }).result?.grants) ?? []
              }
            }
            
            const privSet = new Set<string>()
            for (const grant of grants) {
              const privName = grant.privilege || 'UNKNOWN'
              privSet.add(privName)
              allPrivileges.add(privName)
            }
            userPrivMap.set(user.user_name, privSet)
          } catch (error) {
            console.error(`Failed to load grants for user ${user.user_name}:`, error)
          }
        }
      }
      
      // Load privileges for each role (PostgreSQL only)
      const rolePrivMap = new Map<string, Set<string>>()
      if (isPostgreSQL && rolesList.length > 0) {
        for (const role of rolesList) {
          if (role.role_name) {
            try {
              const grantsResponse = await AgentProxyService.getGrants(
                connectionId,
                selectedDatabase,
                role.role_name,
                'role'
              )
              
              let grants: Grant[] = []
              if (grantsResponse && typeof grantsResponse === 'object') {
                if ('grants' in grantsResponse) {
                  grants = ((grantsResponse as { grants?: Grant[] }).grants) ?? []
                } else if ('result' in grantsResponse && (grantsResponse as { result?: { grants?: Grant[] } }).result?.grants) {
                  grants = ((grantsResponse as { result?: { grants?: Grant[] } }).result?.grants) ?? []
                }
              }
              
              const privSet = new Set<string>()
              for (const grant of grants) {
                const privName = grant.privilege || 'UNKNOWN'
                privSet.add(privName)
                allPrivileges.add(privName)
              }
              rolePrivMap.set(role.role_name, privSet)
            } catch (error) {
              console.error(`Failed to load grants for role ${role.role_name}:`, error)
            }
          }
        }
      }
      
      setUserPrivileges(userPrivMap)
      setRolePrivileges(rolePrivMap)
      setPrivileges(Array.from(allPrivileges).sort())
      
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      showToast('error', 'Failed to load RBAC data', message)
    } finally {
      setIsLoading(false)
    }
  }, [connectionId, selectedDatabase, isPostgreSQL, showToast])
  
  // Handle privilege toggle for users
  const handleUserPrivilegeToggle = useCallback(async (userName: string, privilege: string) => {
    if (!connectionId || !selectedDatabase) return
    
    setIsLoading(true)
    try {
      const hasPrivilege = userPrivileges.get(userName)?.has(privilege) || false
      
      if (hasPrivilege) {
        // Revoke privilege
        await AgentProxyService.revokePrivileges(connectionId, selectedDatabase, userName, {
          privileges: [privilege],
          object_type: 'database',
          object_name: selectedDatabase
        })
        showToast('success', `Revoked ${privilege} from user ${userName}`)
      } else {
        // Grant privilege
        await AgentProxyService.grantPrivileges(connectionId, selectedDatabase, userName, {
          privileges: [privilege],
          object_type: 'database',
          object_name: selectedDatabase
        })
        showToast('success', `Granted ${privilege} to user ${userName}`)
      }
      
      // Reload data to reflect changes
      await loadData()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      showToast('error', 'Failed to update privilege', message)
    } finally {
      setIsLoading(false)
    }
  }, [connectionId, selectedDatabase, userPrivileges, loadData, showToast])
  
  // Handle privilege toggle for roles
  const handleRolePrivilegeToggle = useCallback(async (roleName: string, privilege: string) => {
    if (!connectionId || !selectedDatabase) return
    
    setIsLoading(true)
    try {
      const hasPrivilege = rolePrivileges.get(roleName)?.has(privilege) || false
      
      if (hasPrivilege) {
        // Revoke privilege
        await AgentProxyService.revokePrivileges(connectionId, selectedDatabase, roleName, {
          privileges: [privilege],
          object_type: 'database',
          object_name: selectedDatabase
        })
        showToast('success', `Revoked ${privilege} from role ${roleName}`)
      } else {
        // Grant privilege
        await AgentProxyService.grantPrivileges(connectionId, selectedDatabase, roleName, {
          privileges: [privilege],
          object_type: 'database',
          object_name: selectedDatabase
        })
        showToast('success', `Granted ${privilege} to role ${roleName}`)
      }
      
      // Reload data to reflect changes
      await loadData()
      } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      showToast('error', 'Failed to update privilege', message)
    } finally {
      setIsLoading(false)
    }
  }, [connectionId, selectedDatabase, rolePrivileges, loadData, showToast])
  
  // Handle role assignment to user
  const handleUserRoleToggle = useCallback(async (userName: string, roleName: string) => {
    if (!connectionId || !selectedDatabase) return
    
    setIsLoading(true)
    try {
      // For role assignment, we need to use grant_role/revoke_role
      // Check if user already has this role - this would require additional API call
      // For now, assume it's a toggle operation
      
      await AgentProxyService.grantRole(connectionId, selectedDatabase, roleName, userName)
      showToast('success', `Assigned role ${roleName} to user ${userName}`)
      
      // Reload data to reflect changes
      await loadData()
      } catch (error) {
      // If granting fails, try revoking instead
        try {
        await AgentProxyService.revokeRole(connectionId, selectedDatabase, roleName, userName)
        showToast('success', `Removed role ${roleName} from user ${userName}`)
        await loadData()
        } catch {
          const message = error instanceof Error ? error.message : 'Unknown error'
          showToast('error', 'Failed to update role assignment', message)
      }
    } finally {
      setIsLoading(false)
    }
  }, [connectionId, selectedDatabase, loadData, showToast])
  
  useEffect(() => {
    if (selectedDatabase) {
      void loadData()
    }
  }, [selectedDatabase, loadData])
  
  if (!dataSource) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400">Connection not found</p>
        </div>
      </div>
    )
  }
  
  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to={`/database-users/${connectionId}`}
                className="flex items-center text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
              >
                <ArrowLeftIcon className="w-4 h-4 mr-2" />
                Back to User Management
              </Link>
              <div className="flex items-center space-x-2">
                <ShieldCheckIcon className="w-5 h-5 text-primary-600" />
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                  RBAC Privilege Matrix
                </h1>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  • {dataSource.displayName} • {dataSource.databaseType}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => void loadData()}
              className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
              title="Refresh"
            >
              <ArrowPathIcon className="w-5 h-5" />
            </button>
          </div>
          
          {/* Database Selection */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Select Database
            </label>
            <select
              value={selectedDatabase}
              onChange={(e) => { setSelectedDatabase(e.target.value) }}
              className="w-full md:w-auto px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              disabled={isLoadingDatabases}
            >
              <option value="">Choose a database...</option>
              {databasesList.map((db) => (
                <option key={db.name} value={db.name}>
                  {db.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      {/* Matrix Content */}
      {!selectedDatabase ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <ShieldCheckIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Database Selected
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              Choose a database to view the RBAC privilege matrix.
            </p>
          </div>
        </div>
      ) : isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="flex-1 overflow-auto p-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="sticky left-0 z-10 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">
                      <div className="flex items-center space-x-2">
                        <UserIcon className="w-4 h-4" />
                        <span>User / Role</span>
                      </div>
                    </th>
                    {privileges.map((privilege) => (
                      <th
                        key={privilege}
                        className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                      >
                        <div className="whitespace-nowrap">{privilege}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {/* Users Section */}
                  {users.length > 0 && (
                    <>
                      <tr key="users-header" className="bg-blue-50 dark:bg-blue-900/20">
                        <td colSpan={privileges.length + 1} className="px-4 py-2">
                          <div className="flex items-center space-x-2 text-sm font-medium text-blue-700 dark:text-blue-300">
                            <UserIcon className="w-4 h-4" />
                            <span>Users</span>
                          </div>
                        </td>
                      </tr>
                      {users.map((user) => (
                        <tr key={user.user_name} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="sticky left-0 z-10 px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white bg-white dark:bg-gray-800">
                            <div className="flex items-center space-x-2">
                              <UserIcon className="w-4 h-4 text-gray-400" />
                              <span>{user.user_name}</span>
                              {isMySQL && user.host && (
                                <span className="text-xs text-gray-500">@{user.host}</span>
                              )}
                            </div>
                          </td>
                          {privileges.map((privilege) => (
                            <td key={privilege} className="px-4 py-3 text-center">
                              <button
                                type="button"
                                onClick={() => { void handleUserPrivilegeToggle(user.user_name, privilege) }}
                                disabled={isLoading}
                                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                                title={`Click to ${userPrivileges.get(user.user_name)?.has(privilege) ? 'revoke' : 'grant'} ${privilege} ${userPrivileges.get(user.user_name)?.has(privilege) ? 'from' : 'to'} ${user.user_name}`}
                              >
                                {userPrivileges.get(user.user_name)?.has(privilege) ? (
                                  <CheckIcon className="w-4 h-4 text-green-500 mx-auto" />
                                ) : (
                                  <XMarkIcon className="w-4 h-4 text-gray-300 mx-auto" />
                                )}
                              </button>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </>
                  )}
                  
                  {/* Roles Section (PostgreSQL only) */}
                  {isPostgreSQL && roles.length > 0 && (
                    <>
                      <tr key="roles-header" className="bg-purple-50 dark:bg-purple-900/20">
                        <td colSpan={privileges.length + 1} className="px-4 py-2">
                          <div className="flex items-center space-x-2 text-sm font-medium text-purple-700 dark:text-purple-300">
                            <UserGroupIcon className="w-4 h-4" />
                            <span>Roles</span>
                          </div>
                        </td>
                      </tr>
                      {roles.map((role) => (
                        <tr key={role.role_name} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="sticky left-0 z-10 px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white bg-white dark:bg-gray-800">
                            <div className="flex items-center space-x-2">
                              <UserGroupIcon className="w-4 h-4 text-gray-400" />
                              <span>{role.role_name}</span>
                            </div>
                          </td>
                          {privileges.map((privilege) => (
                            <td key={privilege} className="px-4 py-3 text-center">
                              <button
                                type="button"
                                onClick={() => { void handleRolePrivilegeToggle(role.role_name, privilege) }}
                                disabled={isLoading}
                                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                                title={`Click to ${rolePrivileges.get(role.role_name)?.has(privilege) ? 'revoke' : 'grant'} ${privilege} ${rolePrivileges.get(role.role_name)?.has(privilege) ? 'from' : 'to'} ${role.role_name}`}
                              >
                                {rolePrivileges.get(role.role_name)?.has(privilege) ? (
                                  <CheckIcon className="w-4 h-4 text-green-500 mx-auto" />
                                ) : (
                                  <XMarkIcon className="w-4 h-4 text-gray-300 mx-auto" />
                                )}
                              </button>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </>
                  )}
                  
                  {users.length === 0 && roles.length === 0 && (
                    <tr key="empty-state">
                      <td colSpan={privileges.length + 1} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                        No users or roles found in this database.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Role Assignment Matrix (PostgreSQL only) */}
          {isPostgreSQL && users.length > 0 && roles.length > 0 && (
            <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">User-Role Assignment Matrix</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Click to assign or revoke roles from users
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr key="role-matrix-header">
                      <th className="sticky left-0 z-10 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">
                        <div className="flex items-center space-x-2">
                          <UserIcon className="w-4 h-4" />
                          <span>User</span>
                        </div>
                      </th>
                      {roles.map((role) => (
                        <th
                          key={role.role_name}
                          className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                        >
                          <div className="whitespace-nowrap flex items-center justify-center space-x-1">
                            <UserGroupIcon className="w-3 h-3" />
                            <span>{role.role_name}</span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {users.map((user) => (
                      <tr key={`role-${user.user_name}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="sticky left-0 z-10 px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white bg-white dark:bg-gray-800">
                          <div className="flex items-center space-x-2">
                            <UserIcon className="w-4 h-4 text-gray-400" />
                            <span>{user.user_name}</span>
                            {isMySQL && user.host && (
                              <span className="text-xs text-gray-500">@{user.host}</span>
                            )}
                          </div>
                        </td>
                        {roles.map((role) => (
                          <td key={role.role_name} className="px-4 py-3 text-center">
                            <button
                              type="button"
                              onClick={() => { void handleUserRoleToggle(user.user_name, role.role_name) }}
                              disabled={isLoading}
                              className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                              title={`Click to assign/revoke role ${role.role_name} ${user.user_name.includes(role.role_name) ? 'from' : 'to'} ${user.user_name}`}
                            >
                              {/* For now, show as not assigned - would need additional API to check role membership */}
                              <XMarkIcon className="w-4 h-4 text-gray-300 mx-auto" />
                            </button>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Legend */}
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Legend & Instructions</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center space-x-2">
                  <CheckIcon className="w-4 h-4 text-green-500" />
                  <span className="text-gray-600 dark:text-gray-400">Has Privilege</span>
                </div>
                <div className="flex items-center space-x-2">
                  <XMarkIcon className="w-4 h-4 text-gray-300" />
                  <span className="text-gray-600 dark:text-gray-400">No Privilege</span>
                </div>
                <div className="flex items-center space-x-2">
                  <UserIcon className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600 dark:text-gray-400">User</span>
                </div>
                {isPostgreSQL && (
                  <div className="flex items-center space-x-2">
                    <UserGroupIcon className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-400">Role</span>
                  </div>
                )}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                <strong className="text-blue-700 dark:text-blue-300">💡 Interactive Matrix:</strong> Click on any privilege icon to grant or revoke privileges. {isPostgreSQL ? 'Use the role assignment matrix below to assign roles to users.' : ''}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}